// The words the death, birth and mortuary screens share
// (docs/plans/phase2-mortuary-and-notifications.md §6). One name for each state everywhere.
import type {
  CollectorIdType,
  DeathCauseRole,
  DeathManner,
  DeathPlace,
  MortuaryCaseStatus,
} from '@wodi/domain';
import type { VitalRequestStatus } from '@wodi/shared';
import type { TagState } from '@wodi/ui';

import { ApiError } from '../api/client.ts';

export const PLACE_WORDS: Record<DeathPlace, string> = {
  ward: 'Ward',
  casualty: 'Casualty',
  brought_in_dead: 'Brought in dead',
  outside_facility: 'Outside this facility',
};

/** TODO(clinical-verify) TODO(legal-verify) D3: provisional words for the provisional list. */
export const MANNER_WORDS: Record<DeathManner, string> = {
  disease: 'Disease',
  accident: 'Accident',
  intentional_self_harm: 'Intentional self-harm',
  assault: 'Assault',
  legal_intervention: 'Legal intervention',
  war: 'War',
  pending_investigation: 'Pending investigation',
  could_not_be_determined: 'Could not be determined',
};

export const CAUSE_ROLE_WORDS: Record<DeathCauseRole, string> = {
  immediate: 'Immediate cause',
  underlying: 'Underlying cause',
  contributing: 'Contributing condition',
};

export const DEATH_STATUS_TAGS: Record<'recorded' | 'entered_in_error', [string, TagState]> = {
  recorded: ['Recorded', 'done'],
  entered_in_error: ['Entered in error', 'neutral'],
};

export const REQUEST_TAGS: Record<VitalRequestStatus, [string, TagState]> = {
  requested: ['Waiting for approval', 'attention'],
  approved: ['Approved', 'done'],
  rejected: ['Rejected', 'failed'],
  withdrawn: ['Withdrawn', 'neutral'],
};

export const CASE_STATUS_TAGS: Record<MortuaryCaseStatus, [string, TagState]> = {
  in_storage: ['In storage', 'active'],
  released: ['Released', 'done'],
  entered_in_error: ['Cancelled', 'neutral'],
};

/** TODO(legal-verify) M2: provisional identity documents. */
export const ID_TYPE_WORDS: Record<CollectorIdType, string> = {
  national_id: 'National ID',
  passport: 'Passport',
  alien_id: 'Alien ID',
  birth_certificate: 'Birth certificate',
  other: 'Other document',
};

/** The line every printed summary carries until the official forms are confirmed. */
export const PROVISIONAL_LINE = 'Provisional summary. Not the official notification form.';

export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function validTime(time: string): boolean {
  return TIME.test(time);
}

/** The EAT wall-clock date and time as an instant (Kenya has no daylight saving). */
export function eatInstant(day: string, time: string): string {
  return new Date(`${day}T${time}:00+03:00`).toISOString();
}

/** Now as an EAT `HH:MM`, for a time field's default. */
export function eatClockNow(now: Date = new Date()): string {
  const eat = new Date(now.getTime() + 3 * 60 * 60_000);
  return `${String(eat.getUTCHours()).padStart(2, '0')}:${String(eat.getUTCMinutes()).padStart(2, '0')}`;
}

export function gramsText(grams: number): string {
  return `${grams.toLocaleString('en-KE')} g`;
}
